"use client";
import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./BlogView.module.css";
import AxiosInstance, { IMAGE_BASE_URL } from "../../services/Api";

const fallbackViewImage =
  "https://lh3.googleusercontent.com/aida-public/AB6AXuC9HQUCpha79m5ojhdmprH7HIa-bp2j9xJONxY55sGxE4tq4jd1iDo8tL1hI__gTulHRcltbIgT_L966JRYjvMLZOugqfgHgJ9BJx_jPjM5QRGRIrVwFAPKuJMtrwow5dWGE3lnwiVWaALPzWmjdxfZKDtzwXnrfxGnurPcJlh4Sguad0GCm-USXmsgI-rEvq_V5Q-NSfDCayqYRUkCZNIB1dCkfrVjr6K1PZO6qQ_WpsOhU2Ep6nfFJtUdhEz-je6dBI2e0FkzyaVm";

const trendingPosts = [
  {
    title: "Building high-performance teams in a remote world",
    dateLabel: "Oct 14",
    href: "#",
  },
  {
    title: "Why Tailwind CSS is winning the utility-first war",
    dateLabel: "Oct 9",
    href: "#",
  },
  {
    title: "The case for monolithic architectures in 2024",
    dateLabel: "Sep 28",
    href: "#",
  },
];

export default function BlogView() {
  const [searchParams] = useSearchParams();
  const blogId = searchParams.get("id");

  const [progress, setProgress] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const copyResetRef = useRef(null);

const coverImage = useMemo(() => {
  const path = blog?.cover_image_path;
  if (!path) return "";
  const rawPath = String(path).trim();

  if (rawPath.startsWith("http://") || rawPath.startsWith("https://")) return rawPath;
  if (rawPath.startsWith("blob:")) return "";

  const base = IMAGE_BASE_URL.replace(/\/+$/, "");        // "https://dev.ifillip.com/api"
  const origin = (() => {
    try { return new URL(base).origin; } catch { return base; }
  })();                                                    // "https://dev.ifillip.com"

  // Stored paths are root-absolute ("/api/uploads/..." or "/public/...").
  // Resolve against ORIGIN so "/api" isn't doubled.
  if (rawPath.startsWith("/")) return `${origin}${rawPath}`;

  // Fallback: a relative path with no leading slash → join to base.
  return `${base}/${rawPath}`;
}, [blog]);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (window.scrollY / totalHeight) * 100;
      setProgress(scrolled);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setShareUrl(
      typeof window !== "undefined" ? window.location.href : ""
    );
  }, []);

  useEffect(() => {
    if (!shareOpen) setCopied(false);
  }, [shareOpen]);

  useEffect(() => {
    if (shareOpen && typeof window !== "undefined") {
      setShareUrl(window.location.href);
    }
  }, [shareOpen]);

  useEffect(() => {
    if (!shareOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShareOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [shareOpen]);

  useEffect(() => {
    return () => {
      if (copyResetRef.current) clearTimeout(copyResetRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!blogId) {
        setError("Invalid blog ID.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const response = await AxiosInstance.post("/blog/view", {
          id: Number(blogId),
        });
        if (response?.data?.status && response?.data?.blog) {
          setBlog(response.data.blog);
        } else {
          setError(response?.data?.message || "Failed to fetch blog details.");
        }
      } catch (err) {
        setError(
          err?.response?.data?.message || err?.message || "Failed to fetch blog details.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [blogId]);

  async function handleCopyShareLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    if (copyResetRef.current) clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className={styles.blogPage}>
      <div
        className={styles.progressBar}
        style={{ width: `${progress}%` }}
      />

      <main>
        <section className={styles.heroSection}>
          <img
            alt="Article Cover"
            className={styles.heroImage}
            src={coverImage || fallbackViewImage}
            onError={(e) => {
              e.currentTarget.src = fallbackViewImage;
            }}
          />
          <div className={styles.heroOverlay} />

          <div className="container">
            <div className={styles.heroCardWrap}>
              <div className={styles.heroCard}>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <span className={styles.tagBadge}>
                    {blog?.categorie_name || "BLOG"}
                  </span>
                  <div className="ms-auto d-flex gap-3">
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Share"
                      aria-expanded={shareOpen}
                      aria-haspopup="dialog"
                      onClick={() => setShareOpen(true)}
                    >
                      <i className="fas fa-share-alt" />
                    </button>
                  </div>
                </div>

                <h1 className={styles.heroTitle}>
                  <span className="gradientText">{blog?.title || "Blog Details"}</span>
                </h1>
                <p className={styles.heroSubtitle}>
                  {blog?.tagline || ""}
                </p>

                <div className={styles.authorRow}>
                  <div>
                    <p className={styles.authorName}>{blog?.author_name || "—"}</p>
                    <p className={styles.authorRole}>{blog?.author_designation || "—"}</p>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.metaInfo}>
                    <span>
                      <i className="far fa-calendar-alt me-1" />{" "}
                      {blog?.posted_date
                        ? new Date(blog.posted_date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                          })
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.contentSection}>
          <div className="container">
            <div className="row g-5">
              <div className="col-lg-8">
                <article className={styles.articleContent}>
                  {loading ? <p className={styles.lead}>Loading blog...</p> : null}
                  {error ? <p className={styles.lead}>{error}</p> : null}
                  {!loading && !error ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: blog?.content || "<p>No content found.</p>" }}
                    />
                  ) : null}
                </article>
              </div>

              <aside className="col-lg-4">
                <div className={styles.stickySidebar}>
                  <div>
                    <h2 className={styles.trendingHeading}>Recent Blogs</h2>
                    <div className={styles.trendingList}>
                      {trendingPosts.map((post, idx) => (
                        <a key={post.title} href={post.href} className={styles.trendingLink}>
                          <span className={styles.trendingIndex}>
                            {String(idx + 1).padStart(2, "0")}
                          </span>
                          <div className={styles.trendingBody}>
                            <h3 className={styles.trendingTitle}>
                              {post.title}
                            </h3>
                            <p className={styles.trendingMeta}>
                              • {post.dateLabel}
                            </p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className={styles.navSection}>
          <div className="container">
            <div className="row g-4">
              <div className="col-md-6">
                <a href="/blog" className={styles.navCardPrev}>
                  <div>
                    <p className={styles.navLabel}>
                      <i className="fas fa-arrow-left me-2" /> BACK
                    </p>
                    <h4 className={styles.navTitle}>Back to Blog List</h4>
                  </div>
                </a>
              </div>
              <div className="col-md-6">
                <a href="/blog" className={styles.navCardNext}>
                  <div>
                    <p className={styles.navLabel}>
                      Explore <i className="fas fa-arrow-right ms-2" />
                    </p>
                    <h4 className={styles.navTitle}>More Blogs</h4>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {shareOpen ? (
        <div
          className={styles.shareBackdrop}
          role="presentation"
          onClick={() => setShareOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-dialog-title"
            className={styles.shareModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.shareModalHeader}>
              <h2 id="share-dialog-title" className={styles.shareModalTitle}>
                Share article
              </h2>
              <button
                type="button"
                className={styles.shareModalClose}
                aria-label="Close"
                onClick={() => setShareOpen(false)}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className={styles.shareRow}>
              <input
                readOnly
                className={styles.shareInput}
                value={shareUrl}
                aria-label="Article link"
              />
              <button
                type="button"
                className={styles.shareCopyBtn}
                onClick={handleCopyShareLink}
                aria-label={copied ? "Copied" : "Copy link"}
              >
                <i className="fas fa-copy" />
              </button>
            </div>
            {copied ? (
              <p className={styles.shareCopied} role="status">
                Copied to clipboard
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}